import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { BaseComponent } from '../component-base';
import { SurveyResponseService } from '../../services/survey-response.service';
import { QuestionWithResponse, SaveSurveyResponse, SurveyQuestionResponse } from '../../models/survey-response.model';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-survey-question-viewer',
  templateUrl: './survey-question-viewer.component.html'
})
export class SurveyQuestionViewerComponent extends BaseComponent implements OnInit, OnDestroy {

  private routeParamsSubscription?: Subscription;
  private _surveyTypeId?: number;
  private _isReadOnly = false;
  surveyId?: string;

  @Input()
  set surveyTypeId(value: number | undefined) {
    this._surveyTypeId = value;
    this.tryLoadQuestions();
  }

  get surveyTypeId(): number | undefined {
    return this._surveyTypeId;
  }

  @Input()
  set readOnly(value: boolean | string | null | undefined) {
    this._isReadOnly = this.toBoolean(value);
  }

  @Input('readonly')
  set nativeReadOnly(value: boolean | string | null | undefined) {
    this._isReadOnly = this.toBoolean(value);
  }

  get readOnly(): boolean {
    return this._isReadOnly;
  }

  @Input() sectionTitleKey = 'survey.evaluationCriteria';

  questions: QuestionWithResponse[] = [];
  isLoading = false;
  isSaving = false;

  readonly questionTypes = {
    selectList: 'SelectList',
    value: 'Value',
    checkBoxes: 'CheckBoxes',
    radioButton: 'RadioButton'
  };

  constructor(
    private surveyResponseService: SurveyResponseService,
    private toastr: ToastrService
  ) {
    super();
  }

  ngOnInit(): void {
    this.routeParamsSubscription = this.activatedRoute.paramMap.subscribe(params => {
      const newSurveyId = params.get('surveyId') ?? params.get('id');
      if (newSurveyId !== this.surveyId) {
        this.surveyId = newSurveyId ?? undefined;
        this.tryLoadQuestions();
      }
    });

    if (!this.surveyId) {
      const snapshotParams = this.activatedRoute.snapshot.paramMap;
      this.surveyId = snapshotParams.get('surveyId') ?? snapshotParams.get('id') ?? undefined;
    }

    this.tryLoadQuestions();
  }

  ngOnDestroy(): void {
    this.routeParamsSubscription?.unsubscribe();
  }

  canLoadQuestions(): boolean {
    return !!this.surveyId && !!this.surveyTypeId;
  }

  private tryLoadQuestions(): void {
    if (this.canLoadQuestions()) {
      this.loadQuestions();
    }
  }

  private loadQuestions(): void {
    if (!this.canLoadQuestions()) {
      return;
    }
    this.isLoading = true;
    this.surveyResponseService.getSurveyQuestions(this.surveyTypeId!)
      .subscribe({
        next: res => {
          if (res.success && res.value) {
            this.questions = res.value.map(q => {
              const selectedIds = q.selectedAnswerIds ?? [];
              const firstSelected = selectedIds.length ? selectedIds[0] : '';

              // try to find matching answer id from the answers list (case-insensitive)
              let selectedAnswerId = firstSelected;
              if (firstSelected && q.answers && q.answers.length) {
                const match = q.answers.find(a => (a.id ?? '').toLowerCase() === (firstSelected ?? '').toLowerCase());
                if (match && match.id) {
                  selectedAnswerId = match.id;
                }
              }

              return {
                ...q,
                selectedAnswerIds: selectedIds,
                selectedAnswer: selectedAnswerId ?? ''
              } as QuestionWithResponse;
            });

          } else {
            this.questions = [];
            this.toastr.warning(res.message ?? this.translate.instant('common.noResultsFound'), '');
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.questions = [];
          this.toastr.error(this.translate.instant('common.errorContent'), '');
        }
      });
  }

  onCheckboxChange(question: QuestionWithResponse, answerId: string, checked: boolean): void {
    if (this.readOnly) {
      return;
    }
    if (!question.selectedAnswerIds) {
      question.selectedAnswerIds = [];
    }
    if (checked) {
      if (!question.selectedAnswerIds.includes(answerId)) {
        question.selectedAnswerIds.push(answerId);
      }
    } else {
      question.selectedAnswerIds = question.selectedAnswerIds.filter(id => id !== answerId);
    }
  }

  onSingleSelectChange(question: QuestionWithResponse, answerId: string): void {
    if (this.readOnly) {
      return;
    }
    question.selectedAnswerIds = answerId ? [answerId] : [];
    question.selectedAnswer = answerId ?? '';
  }

  onSelectListChange(question: QuestionWithResponse, event: any): void {
    if (this.readOnly) {
      return;
    }
    const selectedValue = event?.target?.value;
    question.selectedAnswerIds = selectedValue ? [selectedValue] : [];
    question.selectedAnswer = selectedValue ?? '';
  }

  onSelectListValueChange(question: QuestionWithResponse, value: string): void {
    if (this.readOnly) {
      return;
    }
    question.selectedAnswer = value ?? '';
    question.selectedAnswerIds = value ? [value] : [];
  }

  trackByQuestion(_index: number, item: QuestionWithResponse): string | undefined {
    return item.id;
  }

  saveResponses(): void {
    if (this.readOnly || !this.canLoadQuestions() || this.isSaving) {
      return;
    }
    
    const payload: SaveSurveyResponse = {
      surveyTypeId: this.surveyTypeId!,
      responses: this.questions.map<SurveyQuestionResponse>(question => {
        const resp: SurveyQuestionResponse = {
          questionId: question.id!
        } as SurveyQuestionResponse;

        if (question.selectedAnswerIds && question.selectedAnswerIds.length) {
          resp.selectedAnswerIds = question.selectedAnswerIds;
        }

        // Only set value if it's a primitive (string/number/boolean). Convert to string because backend expects a string.
        if (question.value !== undefined && question.value !== null && typeof question.value !== 'object') {
          resp.value = String(question.value);
        }

        return resp;
      })
    };

    // debug payload
    // eslint-disable-next-line no-console
    console.log('Saving responses payload:', payload);

    this.isSaving = true;
    this.surveyResponseService.saveResponses(payload).subscribe({
      next: res => {
        this.isSaving = false;
        if (res.success) {
          this.toastr.success(this.translate.instant('common.saveSuccess'), '');
          this.loadQuestions();
        } else {
          this.toastr.error(res.message ?? this.translate.instant('common.errorContent'), '');
        }
      },
      error: () => {
        this.isSaving = false;
        this.toastr.error(this.translate.instant('common.errorContent'), '');
      }
    });
  }

  // Compare function used by select [compareWith] to match option values regardless of case
  compareSelected = (option: any, value: any): boolean => {
    if (option === null && value === null) return true;
    if (option === undefined && value === undefined) return true;
    if (option === null || value === null || option === undefined || value === undefined) return false;
    try {
      return String(option).toLowerCase() === String(value).toLowerCase();
    } catch {
      return option === value;
    }
  };

  private toBoolean(value: boolean | string | null | undefined): boolean {
    if (value === '' || value === null || value === undefined) {
      return value === '';
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }

    return !!value;
  }
}

